import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { setGTMUserProperties, trackUserEvent, setUserContextInWindow } from '../utils/userTracking';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  organizationId: string;
  organizationRole: string;
  companyName?: string;
  companySize?: string;
  companyIndustry?: string;
  companyDescription?: string;
  onboardingCompleted: boolean;
  themePreference?: 'light' | 'dark';
  notificationPreferences?: Record<string, boolean>;
  createdAt: string;
  phone?: string;
  city?: string;
  country?: string;
  location?: string;
  bio?: string;
  website?: string;
  timezone?: string;
  avatar?: string;
  avatarUrl?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  marketingGoals?: string[];
  updatedAt?: string;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, userData?: any) => Promise<{ user: User | null; error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: any) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  console.log('🔍 AuthContext state:', {
    hasUser: !!user,
    hasUserProfile: !!userProfile,
    loading,
    userEmail: user?.email,
    onboardingCompleted: userProfile?.onboardingCompleted
  });

  // Update window context whenever user or profile changes
  useEffect(() => {
    setUserContextInWindow(user, userProfile);
  }, [user, userProfile]);

  // Fetch user profile from backend with retry logic
  const fetchUserProfile = async (authToken: string, currentUser: User, retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second
    
    try {
      console.log(`🔄 AuthContext: Fetching user profile... (attempt ${retryCount + 1})`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ AuthContext: Received user profile data:', data.user);
        console.log('🎯 AuthContext: Onboarding status:', data.user?.onboardingCompleted);
        setUserProfile(data.user);
        
        // Set GTM user properties when profile is loaded (with a slight delay to ensure state is updated)
        setTimeout(() => {
          setGTMUserProperties(currentUser, data.user);
        }, 100);
        
      } else if (response.status === 404 && retryCount < maxRetries) {
        // Profile not found yet (common after registration), retry
        console.log(`⏳ Profile not found yet, retrying in ${retryDelay}ms...`);
        setTimeout(() => {
          fetchUserProfile(authToken, currentUser, retryCount + 1);
        }, retryDelay);
        return;
      } else {
        console.error('❌ Failed to fetch user profile:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('❌ Response body:', errorText);
        
        if (retryCount < maxRetries) {
          console.log(`⏳ Retrying profile fetch in ${retryDelay}ms...`);
          setTimeout(() => {
            fetchUserProfile(authToken, currentUser, retryCount + 1);
          }, retryDelay);
          return;
        }
        
        setUserProfile(null);
      }
    } catch (error) {
      console.error('❌ Error fetching user profile:', error);
      
      if (error instanceof Error && error.name === 'AbortError') {
        console.error('❌ Profile fetch timed out');
      }
      
      if (retryCount < maxRetries) {
        console.log(`⏳ Retrying profile fetch in ${retryDelay}ms...`);
        setTimeout(() => {
          fetchUserProfile(authToken, currentUser, retryCount + 1);
        }, retryDelay);
        return;
      }
      
      setUserProfile(null);
    }
  };

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);
      
      // Fetch user profile if session exists
      if (session?.access_token && session?.user) {
        await fetchUserProfile(session.access_token, session.user);
      }
      
      setLoading(false);
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        const previousUser = user;
        const newUser = session?.user ?? null;
        
        setSession(session);
        setUser(newUser);

        // Handle different auth events for tracking
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('User signed in:', session.user.email);
          
          // Fetch user profile when signed in
          if (session.access_token) {
            await fetchUserProfile(session.access_token, session.user);
          }
          
          // Track login event with user data
          trackUserEvent('login', {
            login_method: 'email_password',
            user_email_domain: session.user.email ? session.user.email.split('@')[1] : null
          }, session.user, null); // Profile not loaded yet
          
        } else if (event === 'SIGNED_OUT') {
          console.log('User signed out');
          
          // Track logout event before clearing user data
          trackUserEvent('logout', {}, previousUser, userProfile);
          
          setUserProfile(null);
          
        } else if (event === 'USER_UPDATED' && session?.user) {
          console.log('User updated');
          
          // Only refresh profile if we have a token and the user actually changed
          if (session.access_token && session.user.id !== previousUser?.id) {
            await fetchUserProfile(session.access_token, session.user);
          }
          
        } else if (!session) {
          setUserProfile(null);
        }

        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []); // Remove user and userProfile from dependencies to prevent infinite loop

  const signUp = async (email: string, password: string, userData?: any) => {
    try {
      // Step 1: Create user with Supabase Auth
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData?.name,
            companyName: userData?.companyName,
            companySize: userData?.companySize,
            companyIndustry: userData?.companyIndustry,
            companyUrl: userData?.companyUrl,
            role: userData?.role,
          }
        }
      });

      if (error) {
        console.error('Supabase Auth signup error:', error);
        return { user: null, error };
      }

      if (!data.user) {
        console.error('No user returned from Supabase Auth');
        return { user: null, error: { message: 'No user created' } as AuthError };
      }

      // Step 2: Get the session token for backend authentication
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        console.error('No access token available after signup');
        return { user: null, error: { message: 'Authentication failed after signup' } as AuthError };
      }

      // Step 3: Complete profile creation via backend
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/complete-signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: userData?.name || '',
            companyName: userData?.companyName || '',
            companySize: userData?.companySize || '',
            companyIndustry: userData?.companyIndustry || '',
            companyUrl: userData?.companyUrl || '',
            role: userData?.role || ''
          })
        });

        if (!response.ok) {
          const errorResult = await response.json();
          console.error('Profile creation failed:', errorResult);
          
          // If profile creation fails, we should clean up the auth user
          // But for now, we'll just return the error
          return { 
            user: null, 
            error: { 
              message: errorResult.message || 'Failed to create user profile' 
            } as AuthError 
          };
        }

        const profileResult = await response.json();
        console.log('Profile created successfully:', profileResult);

        // Track signup event with user data
        if (data.user) {
          trackUserEvent('signup', {
            signup_method: 'email_password',
            user_email_domain: data.user.email ? data.user.email.split('@')[1] : null,
            company_size: userData?.companySize,
            company_industry: userData?.companyIndustry,
            user_role: userData?.role
          }, data.user, null); // Profile not loaded yet
        }

        // Wait a moment for backend processing, then fetch profile
        setTimeout(() => {
          if (data.user && token) {
            fetchUserProfile(token, data.user);
          }
        }, 1000);

        // If user is returned but not confirmed, they need to verify email
        if (data.user && !data.user.email_confirmed_at) {
          console.log('User created, email verification required');
        }

        return { user: data.user, error: null };

      } catch (fetchError) {
        console.error('Network error during profile creation:', fetchError);
        return { 
          user: null, 
          error: { 
            message: 'Network error during profile creation' 
          } as AuthError 
        };
      }

    } catch (error) {
      console.error('Sign up error:', error);
      return { user: null, error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Sign in error:', error);
        return { user: null, error };
      }

      return { user: data.user, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { user: null, error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error);
        return { error };
      }
      
      // Clear user profile
      setUserProfile(null);
      
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error: error as AuthError };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        console.error('Reset password error:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Reset password error:', error);
      return { error: error as AuthError };
    }
  };

  const updateProfile = async (updates: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        return { error: { message: 'No active session' } as AuthError };
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorResult = await response.json();
        return { 
          error: { 
            message: errorResult.message || 'Failed to update profile' 
          } as AuthError 
        };
      }

      const result = await response.json();
      console.log('Profile update result:', result);
      
      // After successful profile update, fetch the updated profile
      if (result.message && result.message.includes('successfully')) {
        await fetchUserProfile(session.access_token, session.user);
      }

      return { error: null };
    } catch (error) {
      console.error('Update profile error:', error);
      return { error: error as AuthError };
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 